import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import {
  useNotificationsItems,
  useNotificationsUnreadCount,
  useNotificationsActions,
} from '../../contexts/NotificationsContext';
import { usePermissions } from '../../utils/usePermissions';
import { resolveNotificationUrl } from '../../utils/notification-routes';
import NotificationItem from './NotificationItem';
import EmptyState from './EmptyState';
import './NotificationInbox.css';

export interface NotificationInboxProps {
  onClose: () => void;
  onNavigate: (url: string) => void;
}

const MAX_VISIBLE = 20;

export const NotificationInbox: React.FC<NotificationInboxProps> = ({
  onClose,
  onNavigate,
}) => {
  const unreadCount = useNotificationsUnreadCount();
  const [tab, setTab] = useState<'unread' | 'all'>(unreadCount > 0 ? 'unread' : 'all');
  const items = useNotificationsItems(tab);
  const { markRead, markAllRead, dismiss } = useNotificationsActions();
  const { hasPermission } = usePermissions();

  const canMarkRead = hasPermission('notifications:mark_read');
  const canDismiss = hasPermission('notifications:dismiss');

  const visibleItems = items.slice(0, MAX_VISIBLE);

  const handleItemClick = async (id: number, entityType: string, entityId: string | number) => {
    if (canMarkRead) {
      await markRead(id);
    }
    const url = resolveNotificationUrl(entityType, entityId);
    if (url) onNavigate(url);
    onClose();
  };

  const handleMarkAll = async () => {
    await markAllRead();
  };

  const handleAllNotifications = () => {
    onNavigate('/notifications');
    onClose();
  };

  const allCount = useNotificationsItems('all').length;

  return (
    <>
      <div className="nd-header">
        <span className="nd-title">Уведомления</span>
        {canMarkRead && unreadCount > 0 && (
          <button type="button" className="nd-markall" onClick={handleMarkAll}>
            Прочитать все
          </button>
        )}
      </div>

      <div className="nd-tabs">
        <button
          type="button"
          className={`nd-tab${tab === 'unread' ? ' is-active' : ''}`}
          onClick={() => setTab('unread')}
        >
          Непрочитанные
          {unreadCount > 0 && (
            <span className="nd-tab-count">{unreadCount}</span>
          )}
        </button>
        <button
          type="button"
          className={`nd-tab${tab === 'all' ? ' is-active' : ''}`}
          onClick={() => setTab('all')}
        >
          Все
          {allCount > 0 && (
            <span className="nd-tab-count">{allCount}</span>
          )}
        </button>
      </div>

      <div className="nd-list" role="list">
        {visibleItems.length === 0 ? (
          <EmptyState variant={tab === 'unread' ? 'no-unread' : 'all-empty'} />
        ) : (
          visibleItems.map((n) => (
            <NotificationItem
              key={n.id}
              notification={n}
              layout="dropdown"
              canDismiss={canDismiss}
              onClick={() => handleItemClick(n.id, n.entity_type, n.entity_id)}
              onDismiss={canDismiss ? async (e) => {
                e.stopPropagation();
                await dismiss(n.id);
              } : undefined}
            />
          ))
        )}
      </div>

      <div className="nd-footer">
        <button type="button" className="nd-footer__link" onClick={handleAllNotifications}>
          Все уведомления
          <ChevronRight size={14} />
        </button>
      </div>
    </>
  );
};

export default NotificationInbox;
